import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TriggerbeeUrlBuilder, TriggerbeeApi } from '../core/triggerbee/triggerbee-url-builder.service';
import { CurrentSiteIdStore } from '../core/current-site-id.store';
import { TargetSite } from '../core/target-site';
import {
  AgentChatMessageRequest,
  AgentChatSuggestionsRequest,
  AgentChatSuggestionsResponse,
  ChatStreamEvent,
  TranscriptionResponse,
} from './agents-chat.model';
import { TurnRating } from './agent-conversation.model';

const CHAT_MESSAGES_ENDPOINT = 'agents/chat/messages';
const CHAT_SUGGESTIONS_ENDPOINT = 'agents/chat/suggestions';
const CHAT_TRANSCRIBE_ENDPOINT = 'agents/chat/transcribe';
const CHAT_RATING_ENDPOINT = 'agents/chat/rating';
const SSE_DATA_PREFIX = 'data:';
const SSE_FRAME_SEPARATOR = '\n\n';

@Injectable({ providedIn: 'root' })
export class AgentsChatService {
  private readonly urlBuilder = inject(TriggerbeeUrlBuilder);
  private readonly currentSiteIdStore = inject(CurrentSiteIdStore);
  private readonly translate = inject(TranslateService);

  /**
   * Opens a streaming chat turn against the Agents microservice (via the Gateway) and emits each
   * server-sent ChatStreamEvent as it arrives. Completes when the turn ends and errors on a
   * transport failure. Unsubscribing aborts the in-flight request.
   *
   * This uses fetch directly instead of HttpRequestDecorator on purpose: Angular's HttpClient
   * cannot read a POST response body incrementally, which is what lets the agent reply render
   * token by token. The URL, credentials (the HttpOnly JWT cookie) and the X-Target-Site-Id header
   * are set here to match what HttpRequestDecorator and TargetSiteInterceptor add on normal calls.
   */
  streamMessage(request: AgentChatMessageRequest): Observable<ChatStreamEvent> {
    return new Observable<ChatStreamEvent>((subscriber) => {
      const abortController = new AbortController();

      this.readStream(request, abortController.signal, (event) => subscriber.next(event))
        .then(() => subscriber.complete())
        .catch((error: unknown) => {
          if (!isAbortError(error)) {
            subscriber.error(error);
          }
        });

      return () => abortController.abort();
    });
  }

  /**
   * Fetches follow-up suggestion chips for a just-finished turn. Best-effort: any failure resolves
   * to an empty list so the caller simply shows no chips. Uses fetch (like the stream) to reuse the
   * same URL, credentials, and X-Target-Site-Id header, and fills the language the same way.
   */
  async getSuggestions(request: AgentChatSuggestionsRequest): Promise<string[]> {
    try {
      const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, CHAT_SUGGESTIONS_ENDPOINT);
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: this.buildHeaders(),
        body: JSON.stringify({ ...request, language: request.language ?? this.resolveLanguage() }),
      });
      if (!response.ok) {
        return [];
      }
      const data = (await response.json()) as AgentChatSuggestionsResponse;
      return Array.isArray(data?.suggestions) ? data.suggestions : [];
    } catch {
      return [];
    }
  }

  // Content-Type is deliberately unset so the browser adds the multipart boundary; credentials and
  // the X-Target-Site-Id header match the chat stream.
  async transcribe(audio: Blob, fileName: string, language?: string): Promise<string> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, CHAT_TRANSCRIBE_ENDPOINT);
    const form = new FormData();
    form.append('file', audio, fileName);
    const lang = language ?? this.resolveTranscriptionLanguage();
    if (lang) {
      form.append('language', lang);
    }
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildMultipartHeaders(),
      body: form,
    });
    if (!response.ok) {
      throw new Error(`Transcription failed with status ${response.status}`);
    }
    const data = (await response.json()) as TranscriptionResponse;
    return data?.text ?? '';
  }

  /**
   * Records the user's rating on one assistant turn. Best-effort and fire-and-forget from the
   * caller's view: resolves true on success, false on any failure (the store then reverts its
   * optimistic state). Reuses the same credentials and X-Target-Site-Id header as the chat stream.
   * The rating is sent as the backend's "Up"/"Down" enum names.
   */
  async rateTurn(conversationId: string, messageId: string, rating: TurnRating, comment?: string): Promise<boolean> {
    try {
      const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, CHAT_RATING_ENDPOINT);
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: this.buildHeaders(),
        body: JSON.stringify({
          conversationId,
          messageId,
          rating: rating === 'up' ? 'Up' : 'Down',
          comment: comment ?? null,
        }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  private async readStream(
    request: AgentChatMessageRequest,
    signal: AbortSignal,
    emit: (event: ChatStreamEvent) => void,
  ): Promise<void> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, CHAT_MESSAGES_ENDPOINT);
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders(),
      body: JSON.stringify({ ...request, language: request.language ?? this.resolveLanguage() }),
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Agents chat request failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      let streaming = true;
      while (streaming) {
        const { value, done } = await reader.read();
        if (done) {
          streaming = false;
        } else {
          buffer += decoder.decode(value, { stream: true });
          buffer = drainFrames(buffer, emit);
        }
      }

      buffer += decoder.decode();
      buffer = drainFrames(buffer, emit);
      if (buffer.trim().length > 0) {
        const event = parseFrame(buffer);
        if (event) {
          emit(event);
        }
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // The stream was already aborted or released; nothing to clean up.
      }
    }
  }

  private buildHeaders(): HeadersInit {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const siteId = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (siteId) {
      headers['X-Target-Site-Id'] = siteId.toString();
    }
    return headers;
  }

  // Like buildHeaders but without Content-Type: the browser must set it so the multipart boundary is included.
  private buildMultipartHeaders(): HeadersInit {
    const headers: Record<string, string> = {};
    const siteId = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (siteId) {
      headers['X-Target-Site-Id'] = siteId.toString();
    }
    return headers;
  }

  // The app's active language, sent so the assistant replies deterministically in it instead of
  // guessing from context. ngx-translate's currentLang is the loaded file code (en/sv); map it to a
  // plain language name the model reads unambiguously. Undefined when translations aren't loaded yet.
  private resolveLanguage(): string | undefined {
    const code = this.translate.currentLang || this.translate.defaultLang;
    switch (code) {
      case 'sv':
        return 'Swedish';
      case 'en':
        return 'English';
      default:
        return code || undefined;
    }
  }

  // ISO-639-1 code (sv / en) for Whisper's `language` hint — unlike resolveLanguage's English name.
  private resolveTranscriptionLanguage(): string | undefined {
    return this.translate.currentLang || this.translate.defaultLang || undefined;
  }
}

// Emits every complete SSE frame in `buffer` and returns the still-incomplete remainder.
function drainFrames(buffer: string, emit: (event: ChatStreamEvent) => void): string {
  let separatorIndex = buffer.indexOf(SSE_FRAME_SEPARATOR);
  while (separatorIndex !== -1) {
    const frame = buffer.slice(0, separatorIndex);
    buffer = buffer.slice(separatorIndex + SSE_FRAME_SEPARATOR.length);
    const event = parseFrame(frame);
    if (event) {
      emit(event);
    }
    separatorIndex = buffer.indexOf(SSE_FRAME_SEPARATOR);
  }
  return buffer;
}

// Parses a single SSE frame into a ChatStreamEvent, or null when it carries no `data:` payload.
function parseFrame(frame: string): ChatStreamEvent | null {
  const payload = frame
    .split('\n')
    .filter((line) => line.startsWith(SSE_DATA_PREFIX))
    .map((line) => line.slice(SSE_DATA_PREFIX.length).replace(/^ /, ''))
    .join('\n');

  if (payload.length === 0) {
    return null;
  }
  return JSON.parse(payload) as ChatStreamEvent;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
